import { safeMartinaSourceHref } from './martinaSourceLinks';

export type MartinaInlinePart =
  | { type: 'text' | 'strong' | 'emphasis'; text: string }
  | { type: 'link'; text: string; href: string };

function closingDelimiter(value: string, start: number, open: string, close: string): number {
  let depth = 0;
  for (let index = start; index < value.length; index += 1) {
    if (value[index] === '\\') { index += 1; continue; }
    if (value[index] === open) depth += 1;
    if (value[index] === close && --depth === 0) return index;
  }
  return -1;
}

export function safeMartinaAnswerHref(value: string): string | null {
  // Preserve the existing canonical Room route as well as the shared CRM/web policy.
  if (/^\/\?room=[1-9]\d*(?:&drawer=1)?$/.test(value)) return value;
  return safeMartinaSourceHref(value);
}

/** React renders the resulting text, never HTML. Nested brackets in real email
 * subjects (for example [External]) are part of the label, not its end. */
export function martinaMarkdownInline(value: string): MartinaInlinePart[] {
  const parts: MartinaInlinePart[] = [];
  let plainStart = 0;
  const plain = (end: number) => {
    if (end > plainStart) parts.push({ type: 'text', text: value.slice(plainStart, end).replace(/\\([\\`*\[\]()|])/g, '$1') });
  };
  for (let index = 0; index < value.length; index += 1) {
    if (value[index] === '\\') { index += 1; continue; }
    if (value[index] === '[') {
      const labelEnd = closingDelimiter(value, index, '[', ']');
      if (labelEnd < 0) break;
      if (value[labelEnd + 1] !== '(') { index = labelEnd; continue; }
      const hrefEnd = closingDelimiter(value, labelEnd + 1, '(', ')');
      if (hrefEnd < 0) break;
      const href = safeMartinaAnswerHref(value.slice(labelEnd + 2, hrefEnd));
      plain(index);
      const text = value.slice(index + 1, labelEnd).replace(/\\([\\[\]()])/g, '$1');
      parts.push(href ? { type: 'link', text, href } : { type: 'text', text });
      index = hrefEnd;
      plainStart = index + 1;
    } else if (value.startsWith('**', index)) {
      const end = value.indexOf('**', index + 2);
      if (end <= index + 2) continue;
      plain(index);
      parts.push({ type: 'strong', text: value.slice(index + 2, end) });
      index = end + 1;
      plainStart = index + 1;
    } else if (value[index] === '*') {
      const end = value.indexOf('*', index + 1);
      const content = end > index + 1 ? value.slice(index + 1, end) : '';
      // A simple emphasis span cannot consume a source link or cross lines:
      // source projection and rendering must keep seeing the same link tokens.
      if (!content || /[\[\]\n]/.test(content)) continue;
      plain(index);
      parts.push({ type: 'emphasis', text: content });
      index = end;
      plainStart = index + 1;
    }
  }
  plain(value.length);
  return parts;
}
